/* ============================================================================
   WOODBRASS BLOG — Product Blocks v12
   Source de vérité unique. À déployer sur assets/blog-products.js.

   Deux blocs saisis dans le corps de l'article, hydratés ici :
     .wb-product-card      mise en avant produit   (maquettes 1f / 2b)
     .wb-comparison-table  tableau comparatif      (maquettes 1d / 2a)

   Source de données, dans cet ordre :
     1. l'îlot serveur <script id="wb-blog-products"> émis par
        snippets/blog-products-data.liquid — note Judge.me, prix formaté par le
        marché, stock, variante, URL préfixée locale. Zéro requête.
     2. à défaut (plus de 20 produits sur la page, handle hors article),
        /products/{handle}.json — pas de note, prix formaté côté client.

   Le markup saisi dans les articles n'a pas changé : data-handle, data-sku,
   data-label, data-handles, data-labels, .wb-attr[data-label], .wb-pros,
   .wb-cons restent lus à l'identique.

   v9 — les lignes de caractéristiques du comparatif se remplissent depuis le
   catalogue quand l'article n'en saisit aucune (voir `attrsDuCatalogue`). La
   saisie prime toujours : un rédacteur qui a choisi ses lignes les garde.

   v10 — mise en avant produit : la marque (`vendor`) s'affiche avant la
   désignation, et la pastille bleue `data-label` posée sur le packshot n'est plus
   rendue. `data-label` reste lu sans effet sur la carte : les 1063 mises en avant
   des articles n'ont RIEN à modifier.

   v11 — les libellés des deux blocs (CTA, Avantages / Inconvénients, stock,
   pastilles, étoiles) sortent du français en dur et suivent la locale : voir
   `TEXTES` plus bas. Rien à modifier dans les articles.

   v12 — lecture mobile (26/10/2026) : la carte rend le rang du classement lu en
   tête de `data-label` (voir `RANG`), et un produit retiré se rend comme un
   intertitre (rang, nom, « non disponible ») au lieu du lien brut. Rien à modifier
   dans les articles.

   v13 — comparatif sans trou (27/10/2026) : une ligne de caractéristique ne
   s'affiche que si CHAQUE produit comparé la remplit, qu'elle vienne du catalogue
   ou de la saisie de l'article. Plus aucune cellule vide, donc plus de tiret de
   remplissage. La liste blanche s'élargit aux attributs Akeneo hors facettes
   (scripts/attrs_liste.py), dont des booléens : « Oui » / « Non » suivent la
   locale (`oui`, `non` de TEXTES). Rien à modifier dans les articles.
   ========================================================================== */

(function () {
  'use strict';

  var CACHE = {};
  var ISLAND = null;
  var ATTRS = {};
  var UID = 0;

  /* Arbitrage des caractéristiques tirées du catalogue, quand l'article n'en saisit
     aucune. Voir migration/build_blog_attrs_whitelist.py pour le choix des attributs
     candidats ; ce qui suit ne peut se décider qu'ici, en connaissant les produits
     réellement comparés. */
  var ATTRS_MAX = 6;            /* au-delà, le tableau cesse d'être lisible */

  /* + / – comme les points forts / faibles des articles (`.wb-procon`), qui ont
     écarté la coche ; couleurs reprises par blog-products.css, section 3. */
  var ICON_PRO = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#067647" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>';
  var ICON_CON = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B42318" stroke-width="2.5" stroke-linecap="round" aria-hidden="true"><path d="M5 12h14"/></svg>';
  var ICON_ARROW = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14"/><path d="M12 5l7 7-7 7"/></svg>';

  /* --- Libellés localisés ------------------------------------------------

     Les deux blocs sont hydratés ici, côté client : leurs libellés ne passent
     donc jamais par `| t` et doivent vivre dans ce fichier. Même repère que
     wb-pt-i18n : la locale se lit sur <html lang>, que le layout alimente
     depuis `request.locale.iso_code`. On ne garde que la langue (de-AT, de-CH,
     fr-BE, nl-BE, pt-PT tombent sur de / fr / nl / pt), et le français sert de
     secours pour toute locale inconnue.

     Les intitulés d'action ne sont PAS le calque du français : chaque langue
     prend la formule que son e-commerce emploie réellement (« Zum Produkt »,
     « Bekijk product », « Ver producto »), sinon le bouton sonne traduit et
     se fait moins cliquer. Les libellés de panier reprennent mot pour mot ceux
     de la fiche produit (`products.product.add_to_cart` des locales du thème) :
     même action, même formulation. */
  var TEXTES = {
    fr: {
      voir: 'Voir le produit', panier: 'Ajouter au panier',
      redaction: 'Le choix de la rédaction',
      enStock: 'En stock', horsStock: 'Indisponible', surCommande: 'Rupture temporaire',
      indispo: 'Produit non disponible', indispoCol: 'Produit indisponible',
      reappro: 'À partir du {d}',
      promo: 'Promo', avantages: 'Avantages', inconvenients: 'Inconvénients',
      comparatif: 'Notre comparatif',
      note: 'Note {v} sur 5', avis: ', {n} avis', decimale: ',',
      oui: 'Oui', non: 'Non'
    },
    en: {
      voir: 'View product', panier: 'Add to cart',
      redaction: "Editor's choice",
      enStock: 'In stock', horsStock: 'Out of stock', surCommande: 'Available on order',
      indispo: 'Product unavailable', indispoCol: 'Product unavailable',
      reappro: 'From {d}',
      promo: 'Sale', avantages: 'Pros', inconvenients: 'Cons',
      comparatif: 'Our comparison',
      note: 'Rated {v} out of 5', avis: ', {n} reviews', decimale: '.',
      oui: 'Yes', non: 'No'
    },
    nl: {
      voir: 'Bekijk product', panier: 'Toevoegen aan winkelwagen',
      redaction: 'Redactiekeuze',
      enStock: 'Op voorraad', horsStock: 'Niet op voorraad', surCommande: 'Beschikbaar op bestelling',
      indispo: 'Product niet beschikbaar', indispoCol: 'Product niet beschikbaar',
      reappro: 'Vanaf {d}',
      promo: 'Aanbieding', avantages: 'Voordelen', inconvenients: 'Nadelen',
      comparatif: 'Onze vergelijking',
      note: 'Beoordeling {v} van 5', avis: ', {n} beoordelingen', decimale: ',',
      oui: 'Ja', non: 'Nee'
    },
    es: {
      voir: 'Ver producto', panier: 'Añadir a la cesta',
      redaction: 'Elección de la redacción',
      enStock: 'En stock', horsStock: 'No disponible', surCommande: 'Disponible bajo pedido',
      indispo: 'Producto no disponible', indispoCol: 'Producto no disponible',
      reappro: 'A partir del {d}',
      promo: 'Oferta', avantages: 'Ventajas', inconvenients: 'Desventajas',
      comparatif: 'Nuestra comparativa',
      note: 'Valoración {v} sobre 5', avis: ', {n} opiniones', decimale: ',',
      oui: 'Sí', non: 'No'
    },
    pt: {
      voir: 'Ver produto', panier: 'Adicionar ao carrinho',
      redaction: 'Escolha da redação',
      enStock: 'Em stock', horsStock: 'Indisponível', surCommande: 'Disponível por encomenda',
      indispo: 'Produto indisponível', indispoCol: 'Produto indisponível',
      reappro: 'A partir de {d}',
      promo: 'Promoção', avantages: 'Vantagens', inconvenients: 'Desvantagens',
      comparatif: 'O nosso comparativo',
      note: 'Avaliação {v} em 5', avis: ', {n} avaliações', decimale: ',',
      oui: 'Sim', non: 'Não'
    },
    it: {
      voir: 'Vedi il prodotto', panier: 'Aggiungi al carrello',
      redaction: 'La scelta della redazione',
      enStock: 'Disponibile', horsStock: 'Non disponibile', surCommande: 'Disponibile su ordinazione',
      indispo: 'Prodotto non disponibile', indispoCol: 'Prodotto non disponibile',
      reappro: 'Dal {d}',
      promo: 'Offerta', avantages: 'Vantaggi', inconvenients: 'Svantaggi',
      comparatif: 'Il nostro confronto',
      note: 'Valutazione {v} su 5', avis: ', {n} recensioni', decimale: ',',
      oui: 'Sì', non: 'No'
    },
    de: {
      voir: 'Zum Produkt', panier: 'In den Warenkorb legen',
      redaction: 'Empfehlung der Redaktion',
      enStock: 'Auf Lager', horsStock: 'Nicht verfügbar', surCommande: 'Auf Bestellung verfügbar',
      indispo: 'Produkt nicht verfügbar', indispoCol: 'Produkt nicht verfügbar',
      reappro: 'Ab {d}',
      promo: 'Angebot', avantages: 'Vorteile', inconvenients: 'Nachteile',
      comparatif: 'Unser Vergleich',
      note: 'Bewertung {v} von 5', avis: ', {n} Bewertungen', decimale: ',',
      oui: 'Ja', non: 'Nein'
    }
  };

  var T = TEXTES[(document.documentElement.getAttribute('lang') || 'fr')
    .toLowerCase().split('-')[0]] || TEXTES.fr;

  /* --- Utilitaires ------------------------------------------------------- */

  /* Le marché FR de la prod vit sous /fr-fr : jamais d'URL en dur. */
  function root() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return r || '/';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function fmt(cents) {
    var n = parseFloat(cents);
    if (isNaN(n)) return '';
    return (n / 100).toFixed(2).replace('.', ',') + ' €';
  }

  /* products.json rend les prix en unités, /cart en centimes : on normalise. */
  function fmtUnits(price) {
    var n = parseFloat(price);
    if (isNaN(n)) return '';
    return n.toFixed(2).replace('.', ',') + ' €';
  }

  function imgSize(src, w) {
    if (!src) return '';
    return src.replace(/\.(jpe?g|png|webp|gif)(\?|$)/i, '_' + w + 'x.$1$2');
  }

  function readIsland() {
    if (ISLAND) return ISLAND;
    ISLAND = {};
    var el = document.getElementById('wb-blog-products');
    if (!el) return ISLAND;
    try {
      ISLAND = JSON.parse(el.textContent || '{}') || {};
    } catch (e) {
      ISLAND = {};
    }
    /* `_attrs` porte la carte clé → [rang, libellé] ; ce n'est pas un produit. */
    ATTRS = ISLAND._attrs || {};
    delete ISLAND._attrs;
    return ISLAND;
  }

  /* --- Résolution produit ------------------------------------------------ */

  function fromIsland(handle) {
    var d = readIsland()[handle];
    if (!d) return null;
    return {
      id: d.id,
      handle: handle,
      title: d.title,
      vendor: d.vendor || '',
      url: d.url,
      img: d.img,
      price: d.price,
      compare: d.compare || '',
      available: d.available !== false,
      stock: d.stock || '',
      stockDate: d.stockDate || '',
      variant: d.variant || null,
      rating: d.rating || 0,
      reviews: d.reviews || 0,
      a: d.a || null
    };
  }

  function normalizeJson(p) {
    if (!p) return null;
    var v = (p.variants && p.variants[0]) || {};
    var compare = v.compare_at_price && parseFloat(v.compare_at_price) > parseFloat(v.price)
      ? fmtUnits(v.compare_at_price) : '';
    var available = typeof p.available === 'boolean'
      ? p.available
      : (p.variants || []).some(function (x) {
          if (typeof x.available === 'boolean') return x.available;
          if (typeof x.inventory_quantity === 'number') return x.inventory_quantity > 0;
          return true;
        });
    var src = (p.image && p.image.src) || (p.images && p.images.length && p.images[0].src) || '';
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      vendor: p.vendor || '',
      url: root() + 'products/' + p.handle,
      img: imgSize(src, 400),
      price: fmtUnits(v.price),
      compare: compare,
      available: available,
      /* products.json n'expose pas les métachamps ERP : pas d'état de stock affiné
         par ce chemin, stockHtml retombe sur `available`. */
      stock: '',
      stockDate: '',
      /* Une seule variante : l'ajout au panier est sans ambiguïté. Sinon, non. */
      variant: (p.variants && p.variants.length === 1 && available) ? v.id : null,
      rating: 0,
      reviews: 0,
      /* products.json n'expose pas les métachamps : pas de caractéristiques par ce chemin. */
      a: null
    };
  }

  function fetchProduct(handle) {
    var hit = fromIsland(handle);
    if (hit) return Promise.resolve(hit);
    var key = 'h:' + handle;
    if (CACHE[key]) return CACHE[key];
    CACHE[key] = fetch(root() + 'products/' + encodeURIComponent(handle) + '.json')
      .then(function (r) { if (!r.ok) throw new Error(handle); return r.json(); })
      .then(function (d) { return normalizeJson(d.product); })
      .catch(function () { return null; });
    return CACHE[key];
  }

  function fetchProductBySku(sku) {
    var key = 's:' + sku;
    if (CACHE[key]) return CACHE[key];
    CACHE[key] = fetch(root() + 'search/suggest.json?q=' + encodeURIComponent(sku) +
        '&resources[type]=product&resources[limit]=5')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var list = d.resources && d.resources.results && d.resources.results.products;
        if (!list || !list.length) return null;
        return fetchProduct(list[0].handle);
      })
      .catch(function () { return null; });
    return CACHE[key];
  }

  /* --- Filet de survie : le handle porté par un vrai lien ----------------- */
  /* L'assainisseur de l'éditeur de texte riche de l'admin supprime les `data-*`
     qu'il ne connaît pas, et il le fait silencieusement : le div reste, vide.
     Un `<a href="/products/…">` traverse l'assainisseur intact (vérifié sur
     neumann-kh-310-ii, 2026-09-15). On relit donc le handle dans le lien quand
     l'attribut a disparu. Les articles antérieurs, eux, gardent leur
     `data-handle` et ne passent jamais par ici. */

  function handleDepuisHref(href) {
    if (!href) return '';
    var i = href.indexOf('/products/');
    if (i < 0) return '';
    var reste = href.slice(i + 10).split('?')[0].split('#')[0].split('/')[0];
    try { reste = decodeURIComponent(reste); } catch (e) { /* href brut */ }
    return reste.trim();
  }

  function handlesDepuisLiens(el, selecteur) {
    return [].slice.call(el.querySelectorAll(selecteur))
      .map(function (a) { return handleDepuisHref(a.getAttribute('href')); })
      .filter(Boolean);
  }

  function getProductFromEl(el) {
    var h = el.getAttribute('data-handle');
    if (!h) h = handlesDepuisLiens(el, 'a[href*="/products/"]')[0] || '';
    if (h) return fetchProduct(h.trim());
    var s = el.getAttribute('data-sku');
    if (s) return fetchProductBySku(s.trim());
    return Promise.resolve(null);
  }

  /* --- Fragments partagés ------------------------------------------------ */

  function starsHtml(p, cls) {
    if (!p.rating) return '';
    var full = Math.round(p.rating);
    if (full < 1) return '';
    if (full > 5) full = 5;
    var glyphs = new Array(full + 1).join('★') + new Array(6 - full).join('☆');
    var label = T.note.replace('{v}', String(p.rating).replace('.', T.decimale)) +
      (p.reviews ? T.avis.replace('{n}', p.reviews) : '');
    return '<span class="' + cls + '">' +
        '<span class="wbb-stars" role="img" aria-label="' + esc(label) + '">' + glyphs + '</span>' +
        (p.reviews ? '<span class="wbb-count">(' + p.reviews + ')</span>' : '') +
      '</span>';
  }

  function pricesHtml(p, wrapCls, priceCls, oldCls) {
    if (!p.price) return '';
    return '<span class="' + wrapCls + '">' +
        '<span class="' + priceCls + (p.compare ? ' ' + priceCls + '--promo' : '') + '">' + esc(p.price) + '</span>' +
        (p.compare ? '<span class="' + oldCls + '">' + esc(p.compare) + '</span>' : '') +
      '</span>';
  }

  /* Réassort : la date arrive en ISO et se met en forme ici, pas en Liquid, qui
     rendrait « 23 February 2027 » sur un article français (`%B` n'est pas
     traduit). Même parade que app_smart_filter_search.js pour les cartes de la
     PLP. Date construite depuis ses composants : « 2027-02-23 » lu en UTC tombe
     la veille sur un décalage négatif. */
  function dateStock(iso) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    if (!m) return '';
    var d = new Date(+m[1], +m[2] - 1, +m[3]);
    try {
      return new Intl.DateTimeFormat(document.documentElement.lang || undefined,
        { day: 'numeric', month: 'long', year: 'numeric' }).format(d);
    } catch (e) {
      return m[3] + '/' + m[2] + '/' + m[1];
    }
  }

  /* Disponibilité : c'est l'îlot Liquid qui tranche, avec la règle de la fiche
     produit (canal web `erp.disponibilites`, puis date de réassort
     `erp.availability_date`). Une variante en `continue` reste « available » à
     zéro de stock : s'y fier affichait « En stock » sur un produit que la fiche
     annonce pour dans cinq mois.

     `order` : en vente sans stock ni donnée ERP (variante en `continue` à zéro).
     Même libellé que la fiche produit, `inventory_groups.order_only`.

     Chemin de secours /products/{handle}.json : pas de métachamps, donc pas de
     `stock`, et on retombe sur `available` comme avant. */
  function stockHtml(p) {
    if (p.stock === 'soon') {
      var quand = dateStock(p.stockDate);
      if (quand) {
        return '<span class="wbb-stock wbb-stock--soon">' +
          esc(T.reappro.replace('{d}', quand)) + '</span>';
      }
    }
    if (p.stock === 'order') {
      return '<span class="wbb-stock wbb-stock--soon">' + T.surCommande + '</span>';
    }
    var enStock = p.stock ? p.stock === 'in' : p.available;
    return enStock
      ? '<span class="wbb-stock">' + T.enStock + '</span>'
      : '<span class="wbb-stock wbb-stock--out">' + T.horsStock + '</span>';
  }

  function shotHtml(p, size) {
    if (!p.img) return '<span class="wbb-shot" aria-hidden="true"></span>';
    return '<span class="wbb-shot"><img src="' + esc(p.img) + '" alt="' + esc(p.title) +
      '" loading="lazy" width="' + size + '" height="' + size + '"></span>';
  }

  /* Marque et désignation, séparées.

     Le titre catalogue ne porte PAS la marque (« Scarlett 2i2 4th Gen », vendor
     « Focusrite ») : c'est pour ça qu'on l'affiche à part. Mais une partie du
     catalogue la répète malgré tout en tête de titre ; dans ce cas on la retire de la
     désignation plutôt que d'écrire « Focusrite / Focusrite Scarlett ». La comparaison
     normalise l'espace insécable, fréquent dans les titres Akeneo. */
  function espaces(s) { return (s || '').replace(/\u00A0/g, ' ').trim(); }

  function brandOf(p) { return espaces(p.vendor); }

  function titleOf(p) {
    var marque = brandOf(p);
    var titre = espaces(p.title);
    if (!marque) return titre;
    if (titre.slice(0, marque.length).toLowerCase() !== marque.toLowerCase()) return titre;
    var reste = titre.slice(marque.length).replace(/^[\s:|–—-]+/, '');
    return reste || titre;
  }

  /* --- Bloc 1 : mise en avant produit (1f / 2b) -------------------------- */

  /* Rang du classement, lu en tête du libellé saisi : « 1. Pioneer DJ DDJ-FLX4 »,
     « N°2 – … ». 325 cartes de 63 tops le portent (26/10/2026) et la carte ne
     l'affichait plus depuis le retrait de la pastille `data-label` (v10) : un top 5
     se lisait sans ses numéros. Deux chiffres au plus, pour qu'une année (« 2025. »)
     ne passe jamais pour un rang. À défaut de `data-label`, le texte du lien de
     repli, qui porte le même libellé. */
  var RANG = /^\s*(?:n[°º]\s*)?(\d{1,2})\s*[.)\-–:]\s*/i;

  function rangDe(el) {
    var m = RANG.exec(el.getAttribute('data-label') || el._wbTexte || '');
    return m ? m[1] : '';
  }

  function rankHtml(rang) {
    return rang ? '<span class="wbp__rank">' + esc(rang) + '</span>' : '';
  }

  /* Produit introuvable ou retiré. Le contenu d'origine porte son nom, souvent dans
     un lien que le chantier des liens 404 a repointé vers une collection : c'est
     l'intertitre du produit dont parle la suite de l'article, il se rend comme tel —
     rang, nom, mention « non disponible ». Il sortait brut dans le cadre de la carte,
     où on le prenait pour un champ vide. Les corps TRADUITS n'ont pas ce contenu (la
     carte y est un `<div>` vide, mesuré sur les 6 langues du top 5 des contrôleurs
     DJ) : le nom vient alors de `data-label`, seul à survivre à la traduction.
     `<template>` : rien ne se charge pendant l'analyse du fragment. */
  function renderFilet(el, rang) {
    var tpl = document.createElement('template');
    tpl.innerHTML = el._wbFilet || '';
    var lien = tpl.content.querySelector('a[href]');
    var nom = (tpl.content.textContent.trim() || el.getAttribute('data-label') || '')
      .replace(RANG, '').trim();
    if (!nom) {
      el.innerHTML = '<div class="wbp wbp--error">' + T.indispo + '</div>';
      return;
    }
    el.innerHTML =
      '<div class="wbp wbp--filet">' +
        rankHtml(rang) +
        '<span class="wbp__filet-body">' +
          (lien
            ? '<a class="wbp__filet-name" href="' + esc(lien.getAttribute('href')) + '">' + esc(nom) + '</a>'
            : '<span class="wbp__filet-name">' + esc(nom) + '</span>') +
          '<span class="wbp__filet-note">' + T.indispo + '</span>' +
        '</span>' +
      '</div>';
  }

  function renderPick(el, p) {
    var rang = rangDe(el);
    if (!p) {
      renderFilet(el, rang);
      return;
    }

    /* ⛔ `data-label` n'est PLUS rendu ici (décision Simon, 31/08/2026) : la pastille
       bleue posée sur le packshot recopiait la désignation dans 1053 des 1063 mises en
       avant du corpus. L'attribut reste lu par le comparatif (`data-labels`), et rien
       n'est à modifier dans les articles. */
    var eyebrow = el.getAttribute('data-eyebrow');
    if (eyebrow !== null) eyebrow = eyebrow.trim() || T.redaction;

    var cart = '';
    if (p.variant) {
      cart = '<form class="wbp__form" action="' + root() + 'cart/add" method="post" enctype="multipart/form-data">' +
          '<input type="hidden" name="id" value="' + esc(p.variant) + '">' +
          '<input type="hidden" name="quantity" value="1">' +
          '<button type="submit" class="wbb-btn wbb-btn--primary">' + T.panier + '</button>' +
        '</form>';
    }

    el.innerHTML =
      '<div class="wbp">' +
        '<div class="wbp__body">' +
          '<div class="wbp__head' + (rang ? ' wbp__head--rang' : '') + '">' +
            (rang ? rankHtml(rang) + '<div class="wbp__ident">' : '') +
            (eyebrow ? '<span class="wbp__eyebrow">' + esc(eyebrow) + '</span>' : '') +
            (brandOf(p) ? '<span class="wbp__brand">' + esc(brandOf(p)) + '</span>' : '') +
            '<h3 class="wbp__title"><a href="' + esc(p.url) + '">' + esc(titleOf(p)) + '</a></h3>' +
            starsHtml(p, 'wbp__rate') +
            (rang ? '</div>' : '') +
          '</div>' +
          '<div class="wbp__meta">' +
            pricesHtml(p, 'wbp__prices', 'wbp__price', 'wbp__old') +
            stockHtml(p) +
            '<div class="wbp__actions">' +
              cart +
              '<a class="wbp__link" href="' + esc(p.url) + '">' + T.voir + ICON_ARROW + '</a>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div class="wbp__media">' +
          '<a href="' + esc(p.url) + '">' + shotHtml(p, 250) + '</a>' +
        '</div>' +
      '</div>';
  }

  /* --- Bloc 2 : tableau comparatif (1d / 2a) ----------------------------- */

  function parseAttrs(el) {
    var out = [];
    el.querySelectorAll('.wb-attr').forEach(function (a) {
      out.push({
        label: a.getAttribute('data-label') || '',
        values: (a.textContent || '').split('|').map(function (v) { return v.trim(); })
      });
    });
    return out;
  }

  function parseList(el, cls) {
    var out = [];
    el.querySelectorAll('.' + cls).forEach(function (a) {
      out.push((a.textContent || '').split('|').map(function (v) { return v.trim(); }));
    });
    return out;
  }

  /* Le libellé d'une ligne déduite du catalogue vient de la liste blanche du
     snippet, qui est en français sur les sept locales. Les mêmes attributs sont
     déjà traduits pour les facettes de la PLP : `wb-blog-attrs-<locale>.js` en
     tire la table, blog-nav.liquid la charge pour la seule locale courante.
     Actif absent ou clé inconnue : on garde le français, comme avant. Les
     lignes saisies dans l'article ne passent jamais par ici, leur libellé étant
     traduit avec le corps. */
  function libelleAttr(cle, defaut) {
    var table = window.WB_BLOG_ATTRS;
    return (table && table[cle]) || defaut;
  }

  /* Caractéristiques déduites du catalogue, quand l'article n'en saisit aucune.
     Trois règles, dans cet ordre :
       1. écarter ce qui n'est pas renseigné sur TOUS les produits comparés :
          une ligne à trous n'apprend rien et troue le tableau ;
       2. écarter les lignes dont toutes les valeurs renseignées sont identiques :
          dans un COMPARATIF, une ligne uniforme ne départage personne ;
       3. classer par spécificité croissante — le rang est le remplissage catalogue de
          l'attribut, donc un attribut rare est une caractéristique de famille et un
          attribut universel est générique — puis plafonner.
     Renvoie le même format que `parseAttrs`, pour que le rendu ne s'en aperçoive pas. */
  function attrsDuCatalogue(products) {
    if (products.length < 2) return [];
    if (products.some(function (p) { return !(p && p.a); })) return [];

    var presence = {};
    products.forEach(function (p) {
      Object.keys(p.a).forEach(function (k) {
        if (p.a[k]) presence[k] = (presence[k] || 0) + 1;
      });
    });

    var lignes = [];
    Object.keys(presence).forEach(function (k) {
      if (presence[k] < products.length) return;
      /* Un booléen sort de l'îlot en « Oui » / « Non » sur toutes les locales. */
      var values = products.map(function (p) {
        var v = p.a[k];
        return v === 'Oui' ? T.oui : v === 'Non' ? T.non : v;
      });
      var distinctes = {};
      values.forEach(function (v) { distinctes[v] = 1; });
      if (Object.keys(distinctes).length < 2) return;
      var meta = ATTRS[k] || [0, k];
      lignes.push({ rang: meta[0], key: k, label: libelleAttr(k, meta[1]),
                    values: values });
    });

    lignes.sort(function (a, b) {
      return a.rang - b.rang || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0);
    });
    return lignes.slice(0, ATTRS_MAX);
  }

  /* rows = [[item p0, item p1, …], …] → items non vides du produit i */
  function itemsFor(rows, i) {
    return rows.map(function (r) { return r[i] || ''; }).filter(Boolean);
  }

  function listHtml(items, icon, mod) {
    if (!items.length) return '';
    return '<div class="wbc__list">' + items.map(function (t) {
      return '<span class="wbc__item' + mod + '">' + icon + '<span>' + esc(t) + '</span></span>';
    }).join('') + '</div>';
  }

  function headCell(p, label) {
    if (!p) return '<th class="wbc__prod" scope="col"><span class="wbc__na">' + T.indispoCol + '</span></th>';
    return '<th class="wbc__prod" scope="col">' +
        '<a href="' + esc(p.url) + '">' +
          shotHtml(p, 100) +
          '<span class="wbc__name">' + esc(p.title) + '</span>' +
          starsHtml(p, 'wbc__rate') +
          pricesHtml(p, 'wbc__prices', 'wbc__price', 'wbc__old') +
          '<span class="wbc__pills">' +
            (label ? '<span class="wbb-pill wbb-pill--blue">' + esc(label) + '</span>' : '') +
            (p.compare ? '<span class="wbb-pill wbb-pill--red">' + T.promo + '</span>' : '') +
          '</span>' +
        '</a>' +
      '</th>';
  }

  function buildDesktop(products, labels, attrs, pros, cons, title, subtitle) {
    var n = products.length;
    var i;

    var head = '<tr><th class="wbc__title" scope="col">' +
        '<span class="wbc__title-t">' + esc(title) + '</span>' +
        (subtitle ? '<span class="wbc__title-s">' + esc(subtitle) + '</span>' : '') +
      '</th>';
    for (i = 0; i < n; i++) head += headCell(products[i], labels[i]);
    head += '</tr>';

    var body = '';
    attrs.forEach(function (a) {
      var cells = '<th class="wbc__lbl" scope="row">' + esc(a.label) + '</th>';
      for (var j = 0; j < n; j++) cells += '<td class="wbc__val">' + esc(a.values[j]) + '</td>';
      body += '<tr>' + cells + '</tr>';
    });

    if (pros.length) {
      var pc = '<th class="wbc__lbl wbc__lbl--strong" scope="row">' + T.avantages + '</th>';
      for (i = 0; i < n; i++) pc += '<td class="wbc__cell">' + listHtml(itemsFor(pros, i), ICON_PRO, '') + '</td>';
      body += '<tr>' + pc + '</tr>';
    }
    if (cons.length) {
      var cc = '<th class="wbc__lbl wbc__lbl--strong wbc__lbl--con" scope="row">' + T.inconvenients + '</th>';
      for (i = 0; i < n; i++) cc += '<td class="wbc__cell">' + listHtml(itemsFor(cons, i), ICON_CON, ' wbc__item--con') + '</td>';
      body += '<tr>' + cc + '</tr>';
    }

    var cta = '<td class="wbc__lbl"></td>';
    for (i = 0; i < n; i++) {
      cta += '<td class="wbc__cta">' + (products[i]
        ? '<a class="wbb-btn wbb-btn--blue wbb-btn--sm" href="' + esc(products[i].url) + '">' + T.voir + ICON_ARROW + '</a>'
        : '') + '</td>';
    }
    body += '<tr class="wbc__row--cta">' + cta + '</tr>';

    return '<div class="wbc__desk"><table class="wbc__table" style="--wbc-cols:' + n + '">' +
        '<thead>' + head + '</thead><tbody>' + body + '</tbody></table></div>';
  }

  function buildMobile(products, labels, attrs, pros, cons, title, subtitle, uid) {
    var tabs = '', sums = '', panels = '';

    products.forEach(function (p, i) {
      if (!p) return;
      var on = i === 0;
      var tabId = uid + '-t' + i;
      var panId = uid + '-p' + i;

      tabs += '<button type="button" class="wbc__tab" role="tab" id="' + tabId + '"' +
        ' aria-controls="' + panId + '" aria-selected="' + (on ? 'true' : 'false') + '"' +
        ' tabindex="' + (on ? '0' : '-1') + '" data-i="' + i + '">' + esc(p.title) + '</button>';

      sums += '<div class="wbc__msum" data-i="' + i + '"' + (on ? '' : ' hidden') + '>' +
          shotHtml(p, 96) +
          '<div class="wbc__msum-body">' +
            (labels[i] ? '<span class="wbb-pill wbb-pill--blue">' + esc(labels[i]) + '</span>' : '') +
            '<span class="wbc__msum-name">' + esc(p.title) + '</span>' +
            starsHtml(p, 'wbc__rate') +
            pricesHtml(p, 'wbc__prices', 'wbc__price', 'wbc__old') +
          '</div>' +
        '</div>';

      var specs = attrs.map(function (a) {
        return '<div class="wbc__mspec"><span class="wbc__mspec-l">' + esc(a.label) + '</span>' +
          '<span class="wbc__mspec-v">' + esc(a.values[i]) + '</span></div>';
      }).join('');

      var proItems = itemsFor(pros, i);
      var conItems = itemsFor(cons, i);

      panels += '<div class="wbc__mpanel" data-i="' + i + '" id="' + panId + '"' +
          ' role="tabpanel" aria-labelledby="' + tabId + '"' + (on ? '' : ' hidden') + '>' +
          (proItems.length ? '<div class="wbc__mgroup"><div class="wbc__mgroup-t">' + T.avantages + '</div>' +
            listHtml(proItems, ICON_PRO, '') + '</div>' : '') +
          (conItems.length ? '<div class="wbc__mgroup wbc__mgroup--con"><div class="wbc__mgroup-t">' + T.inconvenients + '</div>' +
            listHtml(conItems, ICON_CON, ' wbc__item--con') + '</div>' : '') +
          (specs ? '<div class="wbc__mspecs">' + specs + '</div>' : '') +
          '<a class="wbb-btn wbb-btn--blue" href="' + esc(p.url) + '">' + T.voir + ICON_ARROW + '</a>' +
        '</div>';
    });

    return '<div class="wbc__mob">' +
        '<div class="wbc__mhead">' +
          '<div class="wbc__mtitle">' + esc(title) + '</div>' +
          (subtitle ? '<div class="wbc__msub">' + esc(subtitle) + '</div>' : '') +
          '<div class="wbc__tabs" role="tablist" aria-label="' + esc(title) + '">' + tabs + '</div>' +
          sums +
        '</div>' + panels +
      '</div>';
  }

  function wireTabs(el) {
    var tabs = [].slice.call(el.querySelectorAll('.wbc__tab'));
    if (!tabs.length) return;

    function select(i, focus) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-i') === String(i);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focus) t.focus();
      });
      el.querySelectorAll('.wbc__msum, .wbc__mpanel').forEach(function (n) {
        n.hidden = n.getAttribute('data-i') !== String(i);
      });
    }

    tabs.forEach(function (t, idx) {
      t.addEventListener('click', function () { select(t.getAttribute('data-i'), false); });
      t.addEventListener('keydown', function (e) {
        var next = e.key === 'ArrowRight' ? idx + 1 : e.key === 'ArrowLeft' ? idx - 1 : -1;
        if (next < 0 || next >= tabs.length) return;
        e.preventDefault();
        select(tabs[next].getAttribute('data-i'), true);
      });
    });
  }

  function renderTable(el, products) {
    var labels = (el.getAttribute('data-labels') || '').split(',').map(function (l) { return l.trim(); });
    var title = (el.getAttribute('data-title') || T.comparatif).trim();
    var subtitle = (el.getAttribute('data-subtitle') || '').trim();
    /* La saisie de l'article prime toujours sur le catalogue : un rédacteur qui a
       choisi ses lignes ne se les fait pas remplacer. */
    var attrs = parseAttrs(el);
    if (!attrs.length) attrs = attrsDuCatalogue(products);
    /* Une ligne ne s'affiche que si chaque produit comparé la remplit. */
    attrs = attrs.filter(function (a) {
      return products.every(function (p, i) { return p && a.values[i]; });
    });
    var pros = parseList(el, 'wb-pros');
    var cons = parseList(el, 'wb-cons');
    var uid = 'wbc-' + (++UID);

    el.innerHTML =
      buildDesktop(products, labels, attrs, pros, cons, title, subtitle) +
      buildMobile(products, labels, attrs, pros, cons, title, subtitle, uid);

    wireTabs(el);
  }

  function resolve(el) {
    var handles = (el.getAttribute('data-handles') || '').split(',')
      .map(function (h) { return h.trim(); }).filter(Boolean);
    if (!handles.length) {
      /* Même filet que la carte. `.wb-ct-liens` d'abord : les colonnes
         points forts / points faibles ne doivent jamais fournir de handle. */
      handles = handlesDepuisLiens(el, '.wb-ct-liens a[href*="/products/"]');
    }
    if (handles.length) return Promise.all(handles.map(fetchProduct));
    var skus = (el.getAttribute('data-skus') || '').split(',')
      .map(function (s) { return s.trim(); }).filter(Boolean);
    if (skus.length) return Promise.all(skus.map(fetchProductBySku));
    return Promise.resolve([]);
  }

  /* --- Amorçage ---------------------------------------------------------- */

  function init() {
    readIsland();

    document.querySelectorAll('.wb-product-card').forEach(function (el) {
      /* Lire AVANT d'effacer : le handle de secours vit dans le contenu. */
      var attendu = getProductFromEl(el);
      el._wbFilet = el.innerHTML;
      el._wbTexte = el.textContent;
      el.innerHTML = '<div class="wbp wbp--loading"></div>';
      attendu.then(function (p) { renderPick(el, p); });
    });

    document.querySelectorAll('.wb-comparison-table').forEach(function (el) {
      resolve(el).then(function (list) {
        if (list.filter(Boolean).length) renderTable(el, list);
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
